import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFirebase } from '../contexts/FirebaseContext';
import '../styles/sidebar.css';

// Import SVG icons as React components from src/images/icons
import { ReactComponent as SentencesIcon } from '../images/icons/sentences.svg';
import { ReactComponent as DailyStoryIcon } from '../images/icons/daily-story.svg';
import { ReactComponent as PlaylistsIcon } from '../images/icons/playlists.svg';
import { ReactComponent as SavedWordsIcon } from '../images/icons/saved-words.svg';
import { ReactComponent as ChatbotIcon } from '../images/icons/chat-bot3.svg';
import { ReactComponent as SettingsIcon } from '../images/icons/settings.svg';
import { ReactComponent as EditProfileIcon } from '../images/icons/edit-profile.svg';
import { ReactComponent as ContactUsIcon } from '../images/icons/contact-us.svg';
import { ReactComponent as FeatureRequestsIcon } from '../images/icons/feature-requests.svg';

const Sidebar = ({ toggleSidebar }) => {
    const { logOut, isAuthenticated, userPlan, planExpiresAt } = useFirebase();
    const navigate = useNavigate();
    const sidebarRef = useRef(null);
    const touchStartX = useRef(null);
    const touchEndX = useRef(null);

    const handleLogout = async () => {
        try {
            await logOut();
            window.location.href = '/';
        } catch (error) {
            console.error('Logout error:', error);
            localStorage.clear();
            window.location.href = '/';
        }
    };

    // Calculate days remaining for trial users
    const calculateDaysRemaining = () => {
        if (!planExpiresAt) return 0;
        
        const now = new Date();
        const expiration = new Date(planExpiresAt);
        const diffTime = expiration - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays > 0 ? diffDays : 0;
    };

    // Touch event handlers for swipe to close
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (!touchStartX.current || !touchEndX.current) return;
        
        const distance = touchStartX.current - touchEndX.current;
        const isLeftSwipe = distance > 50; // Minimum swipe distance of 50px
        
        if (isLeftSwipe) {
            toggleSidebar();
        }
        
        // Reset values
        touchStartX.current = null;
        touchEndX.current = null;
    };

    // Add overlay when component mounts
    useEffect(() => {
        // Check if we're on mobile before showing overlay
        const isMobile = window.innerWidth <= 768;
        
        // Create overlay element if it doesn't exist
        let overlay = document.querySelector('.sidebar-overlay');
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.className = 'sidebar-overlay';
            document.body.appendChild(overlay);
        }
        
        // Show overlay only on mobile
        if (isMobile) {
            overlay.classList.add('visible');
        } else {
            overlay.classList.remove('visible');
        }
        
        // Add click event to close sidebar when overlay is clicked
        const handleOverlayClick = () => {
            toggleSidebar();
        };
        overlay.addEventListener('click', handleOverlayClick);
        
        // Add resize listener to handle responsive changes
        const handleResize = () => {
            const isMobileNow = window.innerWidth <= 768;
            if (isMobileNow) {
                overlay.classList.add('visible');
            } else {
                overlay.classList.remove('visible');
            }
        };
        
        window.addEventListener('resize', handleResize);
        
        // Cleanup function
        return () => {
            overlay.classList.remove('visible');
            overlay.removeEventListener('click', handleOverlayClick);
            window.removeEventListener('resize', handleResize);
        };
    }, [toggleSidebar]);

    // Render Sidebar only if authenticated
    if (!isAuthenticated && !localStorage.getItem('access_token')) return null;

    return (
        <div 
            className="sidebar-container"
            ref={sidebarRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <h2 className="sidebar-logo">Langjee</h2>
            <nav>
                <ul className="sidebar-nav-list">
                    <li className="sidebar-nav-item">
                        <SentencesIcon className="sidebar-icon" />
                        <Link to="/" className="sidebar-nav-link">Sentences</Link>
                    </li>
                    <li className="sidebar-nav-item">
                        <DailyStoryIcon className="sidebar-icon" />
                        <Link to="/stories" className="sidebar-nav-link">Daily Story</Link>
                    </li>
                    <li className="sidebar-nav-item">
                        <PlaylistsIcon className="sidebar-icon" />
                        <Link to="/playlists" className="sidebar-nav-link">Playlists</Link>
                    </li>
                    <li className="sidebar-nav-item">
                        <SavedWordsIcon className="sidebar-icon" />
                        <Link to="/saved-words" className="sidebar-nav-link">Saved Words</Link>
                    </li>
                    {userPlan !== 'pro' && (
                        <li className="sidebar-nav-item">
                            <ChatbotIcon className="sidebar-icon" />
                            <Link to="/chatbot" className="sidebar-nav-link">Bojee</Link>
                        </li>
                    )}
                    <li className="sidebar-nav-item">
                        <SettingsIcon className="sidebar-icon" />
                        <Link to="/settings" className="sidebar-nav-link">Settings</Link>
                    </li>
                    <li className="sidebar-nav-item">
                        <EditProfileIcon className="sidebar-icon" />
                        <Link to="/profile" className="sidebar-nav-link">Edit Profile</Link>
                    </li>
                    <div className="sidebar-separator"></div>
                    <li className="sidebar-nav-item">
                        <ContactUsIcon className="sidebar-icon" />
                        <Link to="/contact-us" className="sidebar-extra-nav-link">Contact Us</Link>
                    </li>
                    <li className="sidebar-nav-item">
                        <FeatureRequestsIcon className="sidebar-icon" />
                        <Link to="/feature-requests" className="sidebar-extra-nav-link">Feature Requests</Link>
                    </li>
                </ul>
            </nav>
            <div className="sidebar-bottom">
                {userPlan === 'trial' && (
                    <div className="sidebar-trial-container">
                        <div className="sidebar-trial-info">
                            Trial ({calculateDaysRemaining()} Days Remaining)
                        </div>
                        <button 
                            className="sidebar-upgrade-button"
                            onClick={() => navigate('/pricing')}
                        >
                            Upgrade
                        </button>
                        <div className="sidebar-separator"></div>
                    </div>
                )}
                <div className="sidebar-logout-container">
                    <button onClick={handleLogout} className="sidebar-logout-button">Sign Out</button>
                </div>
                <div className="sidebar-separator"></div>
                <footer className="sidebar-footer">
                    Langjee © {new Date().getFullYear()}<br />All rights reserved.
                </footer>
            </div>
        </div>
    );
};

export default Sidebar;
